// Spider/Radar chart for overall skin balance visualization
// Displays 6 key metrics on a hexagonal chart

import { useMemo } from 'react'
import { motion } from 'framer-motion'
import type { MetricKey, MetricDetail } from '@/types/analysis'
import { RADAR_CHART_METRICS } from '@/data/metricExplanations'

interface RadarChartProps {
  metrics: Record<MetricKey, MetricDetail>
  size?: number
  animated?: boolean
}

export function RadarChart({ metrics, size = 280, animated = true }: RadarChartProps) {
  const center = size / 2
  const radius = size * 0.38 // Leave room for labels

  // Get the 6 metrics for radar chart
  const chartMetrics = useMemo(() => {
    return RADAR_CHART_METRICS.map((metricKey) => ({
      metricKey,
      ...metrics[metricKey],
      // Higher score = better skin condition = larger polygon
      displayValue: metrics[metricKey].score,
    }))
  }, [metrics])

  // Calculate points for polygon
  const getPoint = (index: number, value: number) => {
    const angle = (Math.PI * 2 * index) / chartMetrics.length - Math.PI / 2
    const r = (value / 100) * radius
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    }
  }

  // Generate polygon path
  const polygonPath = useMemo(() => {
    return chartMetrics
      .map((m, i) => {
        const point = getPoint(i, m.displayValue)
        return `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
      })
      .join(' ') + ' Z'
  }, [chartMetrics, center, radius])

  // Generate grid lines (background)
  const gridLevels = [20, 40, 60, 80, 100]
  const gridPaths = gridLevels.map((level) => {
    return chartMetrics
      .map((_, i) => {
        const point = getPoint(i, level)
        return `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
      })
      .join(' ') + ' Z'
  })

  // Axis lines from center to each vertex
  const axisLines = chartMetrics.map((_, i) => {
    const point = getPoint(i, 100)
    return `M ${center} ${center} L ${point.x} ${point.y}`
  })

  // Label positions (slightly outside the chart)
  const labelPositions = chartMetrics.map((m, i) => {
    const point = getPoint(i, 115) // Position labels beyond the chart
    return {
      ...point,
      name: m.name,
      score: m.score,
    }
  })

  return (
    <div className="relative" style={{ width: size, height: size + 40 }}>
      <svg width={size} height={size} className="overflow-visible">
        {/* Grid background */}
        {gridPaths.map((path, i) => (
          <path
            key={`grid-${i}`}
            d={path}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={1}
            opacity={0.6}
          />
        ))}

        {/* Axis lines */}
        {axisLines.map((d, i) => (
          <path
            key={`axis-${i}`}
            d={d}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={1}
            opacity={0.6}
          />
        ))}

        {/* Data polygon */}
        <motion.path
          d={polygonPath}
          fill="rgba(70, 192, 198, 0.25)"
          stroke="#46C0C6"
          strokeWidth={2.5}
          initial={animated ? { opacity: 0, scale: 0 } : undefined}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ transformOrigin: `${center}px ${center}px` }}
        />

        {/* Data points */}
        {chartMetrics.map((m, i) => {
          const point = getPoint(i, m.displayValue)
          return (
            <motion.circle
              key={m.metricKey}
              cx={point.x}
              cy={point.y}
              r={5}
              fill="#46C0C6"
              stroke="white"
              strokeWidth={2}
              initial={animated ? { opacity: 0, scale: 0 } : undefined}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.05 }}
            />
          )
        })}

        {/* Labels */}
        {labelPositions.map((label, i) => {
          // Adjust text anchor based on position
          let textAnchor: 'start' | 'middle' | 'end' = 'middle'
          if (label.x < center - 10) textAnchor = 'end'
          else if (label.x > center + 10) textAnchor = 'start'

          return (
            <g key={`label-${i}`}>
              <text
                x={label.x}
                y={label.y - 8}
                textAnchor={textAnchor}
                className="text-xs font-medium fill-gray-700"
              >
                {label.name}
              </text>
              <text
                x={label.x}
                y={label.y + 8}
                textAnchor={textAnchor}
                className="text-xs fill-gray-500"
              >
                {label.score}%
              </text>
            </g>
          )
        })}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: 'rgba(70, 192, 198, 0.25)', border: '2px solid #46C0C6' }}
          />
          Your Score
        </span>
      </div>
    </div>
  )
}
